import React from "react";
import { Drawer, Table, Tag, Popover, Tooltip } from "antd";
import { InfoCircleOutlined } from "@ant-design/icons";
import { ColumnsType } from "antd/es/table";

interface AwardedMaterial {
  materialName: string;
  askedQty: number;
  awardedQty: number;
  reason: string;
  awardedPercentage: number;
  initialPrice: number;
  bestPrice: number;
  taxType: string;
  taxPercentage: number;
  lineTotal: number;
  currency: string;
  status: string;
}

interface BAFODetailsDrawerProps {
  visible: boolean;
  onClose: () => void;
  supplierName: string;
  awardedMaterials: AwardedMaterial[];
}

const BAFODetailsDrawer: React.FC<BAFODetailsDrawerProps> = ({
  visible,
  onClose,
  supplierName,
  awardedMaterials,
}) => {
  const getStatusColor = (status: string) => {
    switch (status) {
      case "Awarded":
        return "green";
      case "Pending":
        return "orange";
      case "Rejected":
        return "red";
      default:
        return "default";
    }
  };

  const columns: ColumnsType<AwardedMaterial> = [
    {
      title: "Material",
      dataIndex: "materialName",
      key: "materialName",
      width: 200,
      fixed: "left",
    },
    {
      title: "Usage",
      dataIndex: "usage",
      key: "usage",
      width: 150,
      ellipsis: true,
      render: (text: any) => {
        const val = text || "-";
        if (val.length > 20) {
          return (
            <Tooltip title={val}>
              <span style={{ cursor: "pointer" }}>{val.slice(0, 20)}...</span>
            </Tooltip>
          );
        }
        return val;
      },
    },
    {
      title: "Asked Qty",
      dataIndex: "askedQty",
      key: "askedQty",
      width: 100,
      align: "center",
    },
    {
      title: "Awarded Qty",
      dataIndex: "awardedQty",
      key: "awardedQty",
      width: 110,
      align: "center",
    },
    {
      title: "Reason",
      dataIndex: "reason",
      key: "reason",
      width: 200,
    },
    {
      title: "Awarded %",
      dataIndex: "awardedPercentage",
      key: "awardedPercentage",
      width: 100,
      align: "center",
      render: (val: number) => `${val}%`,
    },
    {
      title: "Initial Price (INR)",
      dataIndex: "initialPrice",
      key: "initialPrice",
      width: 140,
      align: "right",
      render: (val: number) => val.toLocaleString("en-IN", { minimumFractionDigits: 2 }),
    },
    {
      title: "Best Price (INR)",
      dataIndex: "bestPrice",
      key: "bestPrice",
      width: 130,
      align: "right",
      render: (val: number) => val.toLocaleString("en-IN", { minimumFractionDigits: 2 }),
    },
    {
      title: "Taxes",
      key: "taxes",
      width: 120,
      align: "right",
      render: (_: any, record: AwardedMaterial) => {
        const taxAmount = record.bestPrice * record.awardedQty * (record.taxPercentage / 100);
        return (
          <div style={{ display: "flex", alignItems: "center", justifyContent: "flex-end", gap: 4 }}>
            <span>{taxAmount.toLocaleString("en-IN", { minimumFractionDigits: 2 })}</span>
            <Popover
              content={
                <div>
                  <div><b>Tax Type:</b> {record.taxType}</div>
                  <div><b>Tax Rate:</b> {record.taxPercentage}%</div>
                  <div>
                    <b>Base Amount:</b>{" "}
                    {(record.bestPrice * record.awardedQty).toLocaleString("en-IN", {
                      minimumFractionDigits: 2,
                    })}
                  </div>
                  <div>
                    <b>Tax Amount:</b>{" "}
                    {taxAmount.toLocaleString("en-IN", { minimumFractionDigits: 2 })}
                  </div>
                </div>
              }
              title="Tax Breakdown"
              placement="left"
            >
              <InfoCircleOutlined style={{ color: "#1890ff", cursor: "pointer" }} />
            </Popover>
          </div>
        );
      },
    },
    {
      title: "Line Total (INR)",
      dataIndex: "lineTotal",
      key: "lineTotal",
      width: 140,
      align: "right",
      render: (val: number) => val.toLocaleString("en-IN", { minimumFractionDigits: 2 }),
    },
    {
      title: "Status",
      dataIndex: "status",
      key: "status",
      width: 100,
      align: "center",
      render: (status: string) => (
        <Tag color={getStatusColor(status)}>{status}</Tag>
      ),
    },
  ];

  return (
    <Drawer
      title={`Awarded Materials - ${supplierName}`}
      placement="right"
      onClose={onClose}
      open={visible}
      width={1100}
    >
      <Table
        dataSource={awardedMaterials}
        columns={columns}
        pagination={false}
        scroll={{ x: "max-content" }}
        size="small"
        bordered
        rowKey="materialName"
      />
    </Drawer>
  );
};

export default BAFODetailsDrawer;
